<template>
  <v-card flat class="limitation-filters pa-3">
    <v-row dense align="center">
      <v-col cols="12" md="4">
        <v-autocomplete
          v-model="producerIds"
          :items="$store.state.producers.all"
          :item-props="producerProps"
          :label="$filters.capitalize($t('producers.label', 1))"
          :loading="$store.state.producers.isSearching"
          prepend-inner-icon="mdi-wind-turbine"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          multiple
          chips
          closable-chips
          return-object
          @update:modelValue="handleChange"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>
                {{
                  $filters.capitalize(
                    $t("search_for", { noun: $t("producers.label", 1) })
                  )
                }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="6" md="3">
        <VueDatePicker
          v-model="dateRange"
          range
          auto-apply
          :enable-time-picker="false"
          :teleport="true"
          :locale="$store.getters.language"
          format="dd/MM/yyyy"
          :placeholder="$filters.capitalize($t('limitations.period'))"
          :disabled="onlyActive"
          @update:model-value="handleChange"
        >
        </VueDatePicker>
      </v-col>

      <v-col cols="6" md="2">
        <v-select
          v-model="selectOrigin"
          :label="$filters.capitalize($t('limitations.origin.label'))"
          :items="origin_options"
          item-title="label"
          item-value="name"
          prepend-inner-icon="mdi-tag-outline"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          multiple
          @update:modelValue="handleChange"
        ></v-select>
      </v-col>

      <v-col cols="12" md="3" class="d-flex align-center justify-end" style="gap: 8px">
        <!-- only the limitations running right now (period = now) -->
        <v-btn
          :color="onlyActive ? 'success' : 'primary'"
          :variant="onlyActive ? 'flat' : 'tonal'"
          density="comfortable"
          :prepend-icon="onlyActive ? 'mdi-check-circle' : 'mdi-play-circle-outline'"
          @click="toggleOnlyActive"
        >
          {{ $filters.capitalize($t("limitations.only_active")) }}
        </v-btn>
        <v-btn
          variant="text"
          size="small"
          icon="mdi-filter-remove-outline"
          :title="$filters.capitalize($t('limitations.reset_filters'))"
          @click="resetFilters"
        ></v-btn>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import moment from "moment";
import utils from "@/mixins/utils";
import filters from "@/mixins/filters";
import { isNone } from "@/functions";

// Default period: the last year up to today
const defaultRange = () => [moment().subtract(1, "year").toDate(), new Date()];

export default {
  mixins: [utils, filters],
  emits: ["filterToP"],
  methods: {
    producerProps(item) {
      if (typeof item !== 'object' || item === null) return;
      return {
        ...item,
        title: item.display_name,
        selection: item.display_name,
        value: item.pk || 'error_item_props'
      };
    },
    toggleOnlyActive() {
      this.onlyActive = !this.onlyActive;
      this.handleChange();
    },
    handleChange() {
      let date_start = this.dateRange?.[0] ?? null;
      let date_end = this.dateRange?.[1] ?? null;
      if (this.onlyActive) {
        // "en cours": active at this instant. The API keeps the limitations
        // whose period contains [start, end], so start = end = now.
        const now = new Date();
        date_start = now;
        date_end = now;
      } else if (date_start != null && date_end != null) {
        // Whole days: the picker returns midnight for both ends, so the
        // last selected day was excluded from the search.
        date_start = new Date(date_start);
        date_start.setHours(0, 0, 0, 0);
        date_end = new Date(date_end);
        date_end.setHours(23, 59, 59, 999);
      }
      const idList = (this.producerIds ?? []).map(item => item.pk);
      const selectOrigin = this.selectOrigin ?? [];
      this.$emit("filterToP", {
        start: date_start,
        end: date_end,
        producer_id: idList,
        origin_options: selectOrigin,
        active_now: this.onlyActive
      });
    },
    resetFilters() {
      this.producerIds = [];
      this.onlyActive = false;
      this.dateRange = defaultRange();
      this.selectOrigin = [];
      this.handleChange();
    },
    selectProdFromQuery(list = this.$store.state.producers.all) {
      const p_id = this.$route.query.producer_id;
      const proddy = list.find(opt => opt.pk == p_id);
      if (proddy) {
        this.producerIds = [proddy];
      }
    },
  },
  watch: {
    "$store.state.producers.all"(list) {
      // Producer given in the URL: the list may be loaded after creation
      if (this.producerIds.length === 0 && !isNone(this.$route.query.producer_id)) {
        this.selectProdFromQuery(list);
        if (this.producerIds.length > 0) this.handleChange();
      }
    },
  },
  created() {
    this.$store.dispatch('limitation_origins/getItems').then((items) => {
      this.origin_options = items;
    });
    this.selectProdFromQuery();
    this.handleChange(); // Load default search parameters
  },
  data() {
    return {
      moduleName: "limitations",
      producerIds: [],
      dateRange: defaultRange(),
      onlyActive: false, // filter on the limitations running right now
      selectOrigin: [],
      origin_options: [],
    };
  }
};
</script>
